import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGardenLayout } from '../hooks/useGardenLayouts';
import {
  Loader2,
  ArrowLeft,
  Calendar,
  Maximize2,
  Sprout,
  Download,
  ExternalLink,
  Leaf,
} from 'lucide-react';

const DIFFICULTY_STYLES: Record<string, string> = {
  beginner: 'bg-primary-100 text-primary-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-orange-100 text-orange-700',
};

export default function GardenLayoutDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { layout, loading, error } = useGardenLayout(slug);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !layout) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 mb-4">{error ?? 'Layout not found.'}</p>
        <Link
          to="/layouts"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all layouts
        </Link>
      </div>
    );
  }

  const formattedDate = new Date(layout.date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-10">
          <Link
            to="/layouts"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            All layouts
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${
                DIFFICULTY_STYLES[layout.difficulty] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {layout.difficulty}
            </span>
            <span className="flex items-center gap-1.5 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1.5 text-sm text-gray-500">
              <Sprout className="h-4 w-4" />
              {layout.season}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-dm-serif mb-4 max-w-3xl">
            {layout.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">{layout.excerpt}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Video embed — 9:16 portrait to match Reels/TikTok */}
            <div className="mx-auto w-full max-w-sm bg-black rounded-xl overflow-hidden aspect-[9/16] shadow-sm">
              <iframe
                src={layout.videoEmbedUrl}
                title={layout.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            {layout.videoCaption && (
              <div className="flex items-center gap-2 -mt-4">
                <a
                  href={layout.videoSourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {layout.videoCaption}
                </a>
              </div>
            )}

            {/* Blog content */}
            <article className="bg-white rounded-xl shadow-sm p-8">
              <div
                className="prose prose-lg max-w-none prose-headings:font-dm-serif prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900"
                dangerouslySetInnerHTML={{ __html: layout.content }}
              />
            </article>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Download card */}
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-6">
              <h2 className="font-semibold text-gray-900 mb-1">Download the Layout</h2>
              <p className="text-sm text-gray-600 mb-4">
                Print-ready diagram with plant placements and measurements.
              </p>
              <a
                href={layout.downloadImageUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                <Download className="h-4 w-4" />
                Download Image
              </a>
            </div>

            {/* Dimensions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Maximize2 className="h-4 w-4 text-primary-500" />
                Bed Dimensions
              </h2>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Size</span>
                <span className="font-medium text-gray-900">{layout.dimensions}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-500">Square footage</span>
                <span className="font-medium text-gray-900">{layout.squareFootage} sq ft</span>
              </div>
            </div>

            {/* Plant list */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Leaf className="h-4 w-4 text-primary-500" />
                Plants in This Layout
              </h2>
              <ul className="space-y-3">
                {layout.plants.map((plant) => (
                  <li key={plant.name} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-gray-900">{plant.name}</span>
                      <span className="text-gray-500 whitespace-nowrap">
                        {plant.quantity}× · {plant.spacing}
                      </span>
                    </div>
                    {plant.notes && (
                      <p className="text-gray-500 text-xs mt-0.5">{plant.notes}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tags */}
            {layout.tags.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                  Tags
                </h2>
                <div className="flex flex-wrap gap-2">
                  {layout.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
